// Breadcrumb trail at the top of authed pages, plus a back control over the last three pages.
import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import { type Crumb } from './breadcrumbs';
import { useNavStack } from '@/hooks/useNavStack';
import './workflow-page-nav.css';

export interface WorkflowPageNavProps {
  crumbs: Crumb[];
}

export default function WorkflowPageNav({ crumbs }: WorkflowPageNavProps) {
  const currentLabel = crumbs.at(-1)?.label ?? 'Home';
  const { stack, goBack, jumpTo } = useNavStack(currentLabel);
  const previous = stack[stack.length - 1];
  const canGoBack = Boolean(previous);
  const [historyOpen, setHistoryOpen] = useState(false);
  const historyWrap = useRef<HTMLDivElement>(null);
  const historyId = useId();

  useEffect(() => {
    if (!historyOpen) return;
    const onDoc = (event: MouseEvent) => {
      if (!historyWrap.current?.contains(event.target as Node)) setHistoryOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setHistoryOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [historyOpen]);

  if (crumbs.length === 0 && !canGoBack) return null;

  const recent = [...stack].reverse();

  return (
    <div className="wf-page-nav">
      <div className="wf-page-nav__back-group" ref={historyWrap}>
        <button
          type="button"
          className="wf-page-nav__back btn-unstyled"
          onClick={goBack}
          disabled={!canGoBack}
          aria-label={previous ? `Go back to ${previous.label}` : 'Go back'}
          title={previous ? `Go back to ${previous.label}` : undefined}
        >
          <ArrowLeft aria-hidden="true" />
        </button>
        {stack.length > 0 ? (
          <>
            <button
              type="button"
              className="wf-page-nav__history-toggle btn-unstyled"
              aria-label="Recent pages"
              aria-expanded={historyOpen}
              aria-haspopup="menu"
              aria-controls={historyId}
              onClick={() => setHistoryOpen((open) => !open)}
            >
              <ChevronDown aria-hidden="true" />
            </button>
            {historyOpen ? (
              <ul id={historyId} className="wf-page-nav__history" role="menu">
                {recent.map((entry, offset) => {
                  const index = stack.length - 1 - offset;
                  return (
                    <li key={`${entry.to}-${index}`} role="none">
                      <button
                        type="button"
                        className="wf-page-nav__history-item btn-unstyled"
                        role="menuitem"
                        onClick={() => {
                          setHistoryOpen(false);
                          jumpTo(index);
                        }}
                      >
                        {entry.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </>
        ) : null}
      </div>

      {crumbs.length > 0 ? (
        <nav className="wf-page-nav__crumbs" aria-label="Breadcrumb">
          <ol className="wf-page-nav__list">
            {crumbs.map((crumb, index) => {
              const last = index === crumbs.length - 1;
              return (
                <li key={`${crumb.label}-${crumb.to}`} className="wf-page-nav__li">
                  {index > 0 ? (
                    <span className="wf-page-nav__sep" aria-hidden="true">
                      /
                    </span>
                  ) : null}
                  <Link
                    className={last ? 'wf-page-nav__current' : 'wf-page-nav__item'}
                    to={crumb.to}
                    aria-current={last ? 'page' : undefined}
                  >
                    {crumb.label}
                  </Link>
                </li>
              );
            })}
          </ol>
        </nav>
      ) : null}
    </div>
  );
}
